import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Sparkles } from 'lucide-react';
import { Tabs, TabsContent } from '@/shared/components/ui/tabs';
import { Textarea } from '@/shared/components/ui/textarea';
import { Button } from '@/shared/components/ui/button';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { cn } from '@/core/lib/utils';
import { useAiQuickCreatePanel, RETRYABLE_ERROR_KEY } from '../hooks/useAiQuickCreatePanel';
import { BlankTaskPanel } from './BlankTaskPanel';
import { AiProposalPreview } from './AiProposalPreview';
import { AiQuickCreateTabsHeader } from './AiQuickCreateTabsHeader';
import { VoiceDictationButton } from './VoiceDictationButton';

export interface AiQuickCreatePanelProps {
  projectId: string;
  boardId: string;
  /** Called when the panel should close: cancel, or a successful create. */
  onClose: () => void;
}

/**
 * Quick-create panel: instruction → propose → editable chip preview → apply.
 *
 * Rendered as popover content anchored to the board's *Agregar tarea* button, which is how
 * the design places it — 460px wide, a 44px tab header, the instruction field, the tinted
 * proposal panel and a two-button footer. Voice dictation records via MediaRecorder.
 */
export function AiQuickCreatePanel({ projectId, boardId, onClose }: AiQuickCreatePanelProps) {
  const { t } = useTranslation('ai');
  const {
    instruction,
    setInstruction,
    proposal,
    options,
    errorKey,
    proposeErrorKey,
    canPropose,
    isProposePending,
    voiceDictation,
    confirmOpen,
    confirmOptions,
    handleConfirm,
    handleCancel,
    handleGenerate,
    runPropose,
    handleApply,
    isApplyPending,
  } = useAiQuickCreatePanel(projectId, boardId, onClose);

  // Opens on the tab the trigger is named after; the AI tab keeps its instruction in the
  // store, so nothing is lost by not landing on it.
  const [tab, setTab] = useState('blank');

  return (
    <div data-testid="ai-quick-create-dialog" className="flex flex-col">
      <Tabs value={tab} onValueChange={setTab} className="gap-0">
        <AiQuickCreateTabsHeader />

        {/* Both tabs stay mounted. Radix unmounts the inactive one by default, and each tab
            owns its own form: switching away and back rebuilt it from the proposal, silently
            reverting every correction the user had just made. `hidden` keeps the inactive one
            out of the layout and out of the accessibility tree. */}
        <TabsContent value="blank" className="m-0" forceMount hidden={tab !== 'blank'}>
          <BlankTaskPanel projectId={projectId} boardId={boardId} onClose={onClose} />
        </TabsContent>

        <TabsContent value="ai" className="m-0 space-y-3 p-4" forceMount hidden={tab !== 'ai'}>
          {/* The mic sits inside the field, top-right, as the design draws it — beside it
              the button was stealing width from the text it dictates into. The right padding
              is what keeps a long instruction from running under it. */}
          <div className="relative">
            <Textarea
              id="ai-quick-create-instruction"
              data-testid="ai-quick-create-instruction"
              rows={2}
              aria-label={t('quickCreate.instructionLabel')}
              className={cn(
                'min-h-[60px] resize-none rounded-[7px] focus-visible:border-primary',
                voiceDictation.isSupported && 'pr-12',
              )}
              placeholder={t('quickCreate.instructionPlaceholder')}
              value={instruction}
              disabled={voiceDictation.isProcessing}
              onChange={(event) => {
                voiceDictation.notifyManualEdit();
                setInstruction(event.target.value);
              }}
            />
            {voiceDictation.isSupported && (
              <div className="absolute top-1.5 right-1.5">
                <VoiceDictationButton
                  state={voiceDictation.state}
                  onStart={voiceDictation.startRecording}
                  onStop={voiceDictation.stopRecording}
                  duration={voiceDictation.duration}
                />
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">{t('quickCreate.instructionHint')}</p>
            <Button
              type="button"
              size="sm"
              data-testid="ai-quick-create-submit"
              onClick={handleGenerate}
              disabled={!canPropose}
            >
              {isProposePending ? (
                <>
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                  {t('quickCreate.generating')}
                </>
              ) : (
                <>
                  <Sparkles className="size-4" aria-hidden />
                  {t('quickCreate.generate')}
                </>
              )}
            </Button>
          </div>

          {errorKey && (
            <div
              role="alert"
              data-testid="ai-quick-create-error"
              className="flex flex-col gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
            >
              <p>{t(errorKey)}</p>
              {proposeErrorKey === RETRYABLE_ERROR_KEY && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="self-start"
                  data-testid="ai-quick-create-retry"
                  onClick={runPropose}
                >
                  {t('quickCreate.retry')}
                </Button>
              )}
            </div>
          )}

          {proposal && (
            <AiProposalPreview
              proposal={proposal}
              options={options}
              projectId={projectId}
              boardId={boardId}
              onConfirm={handleApply}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Mounted only while asking. The panel lives inside a Popover, and a Dialog
          kept mounted next to it takes the focus scope away from the fields behind. */}
      {confirmOpen && (
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={(value) => {
            if (!value) handleCancel();
          }}
          title={confirmOptions?.title ?? ''}
          description={confirmOptions?.description}
          confirmLabel={confirmOptions?.confirmLabel}
          cancelLabel={confirmOptions?.cancelLabel}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}

      {/* Footer: separator, then the two buttons the design shows, right aligned. No third
          action — regenerating is re-running Generar. Outside TabsContent so the border runs
          edge to edge, hence the tab check: the blank tab brings its own footer. */}
      {tab === 'ai' && (
        <div className="flex justify-end gap-2.5 border-t px-4 py-3.5">
          <Button
            type="button"
            variant="outline"
            data-testid="ai-quick-create-cancel"
            onClick={onClose}
          >
            {t('quickCreate.cancel')}
          </Button>
          <Button
            type="submit"
            form="ai-draft-form"
            data-testid="ai-quick-create-confirm"
            disabled={!proposal || isApplyPending}
          >
            {isApplyPending ? t('quickCreate.confirmPending') : t('quickCreate.confirm')}
          </Button>
        </div>
      )}
    </div>
  );
}
